import React, { useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import { formatDate } from '../shared/formatting';
import { RichTextEditor } from '../shared/RichTextEditor';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

type MailingList = { id: number; name: string; slug: string; source_group_email: string | null; member_count: number };
type Member = { id: number; name: string | null; email: string; member_type: string | null; unsubscribed: boolean };
type Campaign = {
    id: number; public_id: string; subject: string; status: string;
    total_recipients: number; sent_count: number; failed_count: number; opened_count: number;
    throttle_seconds: number; created_at: string | null; started_at: string | null; finished_at: string | null;
};
type SendRow = { id: number; name: string | null; email: string; status: string; sent_at: string | null; opened_at: string | null; open_count: number; failure_note: string | null };
type CampaignDetail = {
    campaign: Campaign & { html_body: string; list_ids: number[]; throttle_seconds: number };
    list_names: Record<string, string>;
    sends: SendRow[];
    stats: { queued: number; sent: number; failed: number; opened: number };
};

const STATUS_LABEL: Record<string, [string, string, string]> = {
    draft: ['📝 Nacrt', 'Draft', '#6b5f3a'],
    queued: ['⏳ U redu', 'Queued', 'var(--ad-acc-3)'],
    sending: ['📤 Šalje se', 'Sending', GOLD],
    paused: ['⏸ Pauzirano', 'Paused', 'var(--ad-acc-3)'],
    sent: ['✓ Poslano', 'Sent', '#1f7a36'],
    cancelled: ['✗ Otkazano', 'Cancelled', '#b14040'],
};

export function MailingModule({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tab, setTab] = useState<'campaigns' | 'lists'>(readUrlParam('tab', 'campaigns') as 'campaigns' | 'lists');
    useUrlSync('tab', tab, tab === 'campaigns');
    const [lists, setLists] = useState<MailingList[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [openCampaign, setOpenCampaign] = useState<number | null>(null);
    const [composing, setComposing] = useState(false);
    const [membersOf, setMembersOf] = useState<{ list: MailingList; members: Member[] } | null>(null);

    const loadLists = () => api<{ lists: MailingList[] }>('/mailing/lists', token).then((r) => setLists(r.lists)).catch(() => undefined);
    const loadCampaigns = () => api<{ campaigns: Campaign[] }>('/mailing/campaigns', token).then((r) => setCampaigns(r.campaigns)).catch(() => undefined);

    useEffect(() => { void loadLists(); void loadCampaigns(); }, [token]);
    // Auto-refresh kampanje svakih 15s (drip napreduje)
    useEffect(() => {
        if (tab !== 'campaigns') return;
        const id = window.setInterval(() => { void loadCampaigns(); }, 15000);
        return () => window.clearInterval(id);
    }, [tab, token]);

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '2px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                {([['campaigns', '📤', t('Kampanje', 'Campaigns')], ['lists', '👥', t('Liste', 'Lists')]] as const).map(([id, emoji, label]) => (
                    <button key={id} type="button" onClick={() => setTab(id)}
                        style={{ padding: '8px 16px', border: 0, background: 'transparent', borderBottom: tab === id ? `3px solid ${GOLD}` : '3px solid transparent', color: tab === id ? BROWN : '#999', fontWeight: tab === id ? 700 : 500, cursor: 'pointer', fontSize: '0.9rem' }}>
                        {emoji} {label}
                    </button>
                ))}
            </div>

            {tab === 'campaigns' ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <strong style={{ color: BROWN }}>{t('Kampanje', 'Campaigns')} ({campaigns.length})</strong>
                        <button type="button" onClick={() => setComposing(true)}
                            style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                            + {t('Nova kampanja', 'New campaign')}
                        </button>
                    </div>
                    {campaigns.length === 0 ? (
                        <p className="muted" style={{ padding: 20, textAlign: 'center' }}>{t('Još nema kampanja.', 'No campaigns yet.')}</p>
                    ) : (
                        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                <thead><tr>
                                    <th>{t('Naslov', 'Subject')}</th>
                                    <th>{t('Status', 'Status')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Primatelji', 'Recipients')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Poslano', 'Sent')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Otvoreno', 'Opened')}</th>
                                    <th>{t('Kreirano', 'Created')}</th>
                                </tr></thead>
                                <tbody>
                                    {campaigns.map((c) => {
                                        const [hr, en, color] = STATUS_LABEL[c.status] ?? [c.status, c.status, '#999'];
                                        const pct = c.total_recipients > 0 ? Math.round(c.sent_count / c.total_recipients * 100) : 0;
                                        const openPct = c.sent_count > 0 ? Math.round(c.opened_count / c.sent_count * 100) : 0;
                                        return (
                                            <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setOpenCampaign(c.id)}>
                                                <td style={{ fontWeight: 600, color: BROWN }}>{c.subject}</td>
                                                <td><span style={{ color, fontWeight: 700 }}>{locale === 'en' ? en : hr}</span></td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{c.total_recipients}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{c.sent_count} <span className="muted">({pct}%)</span>{c.failed_count > 0 ? <span style={{ color: '#b14040' }}> ✗{c.failed_count}</span> : null}</td>
                                                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#1f7a36' }}>{c.opened_count} <span className="muted">({openPct}%)</span></td>
                                                <td className="muted">{c.created_at ? formatDate(c.created_at, locale) : '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : null}

            {tab === 'lists' ? (
                <>
                    <strong style={{ color: BROWN, display: 'block', marginBottom: 12 }}>{t('Mailing liste', 'Mailing lists')} ({lists.length})</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                        {lists.map((l) => (
                            <button key={l.id} type="button"
                                onClick={() => api<{ members: Member[] }>(`/mailing/lists/${l.id}/members`, token).then((r) => setMembersOf({ list: l, members: r.members }))}
                                style={{ textAlign: 'left', padding: 12, background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 8, cursor: 'pointer' }}>
                                <div style={{ fontWeight: 700, color: BROWN }}>{l.name}</div>
                                {l.source_group_email ? <div style={{ fontSize: '0.74rem', color: '#888', fontFamily: 'monospace' }}>{l.source_group_email}</div> : null}
                                <div style={{ marginTop: 6, fontSize: '0.82rem', color: GOLD, fontWeight: 700 }}>👤 {l.member_count} {t('članova', 'members')}</div>
                            </button>
                        ))}
                    </div>
                </>
            ) : null}

            {composing ? <ComposeModal token={token} locale={locale} lists={lists} onClose={() => setComposing(false)} onCreated={() => { setComposing(false); void loadCampaigns(); }} /> : null}
            {openCampaign !== null ? <CampaignDrawer campaignId={openCampaign} token={token} locale={locale} onClose={() => setOpenCampaign(null)} onChanged={() => void loadCampaigns()} /> : null}
            {membersOf ? <MembersModal data={membersOf} locale={locale} onClose={() => setMembersOf(null)} /> : null}
        </div>
    );
}

function ComposeModal({ token, locale, lists, onClose, onCreated }: { token: string; locale: Locale; lists: MailingList[]; onClose: () => void; onCreated: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [picked, setPicked] = useState<Set<number>>(new Set());
    const [throttle, setThrottle] = useState(60);
    const [fromName, setFromName] = useState('');
    const [replyTo, setReplyTo] = useState('');
    const [busy, setBusy] = useState(false);

    const toggle = (id: number) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const recipientCount = lists.filter((l) => picked.has(l.id)).reduce((acc, l) => acc + l.member_count, 0);

    const create = async () => {
        if (!subject.trim() || !body.trim()) { alert(t('Naslov i sadržaj su obavezni.', 'Subject and body required.')); return; }
        if (picked.size === 0) { alert(t('Odaberi barem jednu listu.', 'Pick at least one list.')); return; }
        setBusy(true);
        try {
            await mutate('/mailing/campaigns', token, 'POST', {
                subject, html_body: body, list_ids: Array.from(picked),
                throttle_seconds: throttle, from_name: fromName || null, reply_to: replyTo || null,
            });
            onCreated();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 20, overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: 'min(820px, 96vw)', marginTop: 20 }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>📤 {t('Nova mailing kampanja', 'New mailing campaign')}</h3>

                <label style={{ display: 'block', marginBottom: 10 }}><span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Naslov (subject)', 'Subject')}</span>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: '100%', padding: 8, marginTop: 3, borderRadius: 6, border: '1px solid var(--ad-acc)' }} />
                </label>

                <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Sadržaj', 'Body')}</span>
                    <p className="muted" style={{ fontSize: '0.72rem', margin: '2px 0 4px' }}>{t('Možeš koristiti {ime} za personalizaciju.', 'Use {ime} for personalization.')}</p>
                    <RichTextEditor value={body} onChange={setBody} locale={locale} minHeight={200} />
                </div>

                <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Liste primatelja', 'Recipient lists')} — {recipientCount} {t('članova (s preklapanjem)', 'members (with overlap)')}</span>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, marginTop: 4, maxHeight: 200, overflowY: 'auto', padding: 8, background: 'var(--ad-bg)', borderRadius: 8 }}>
                        {lists.map((l) => (
                            <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} />
                                {l.name} <span className="muted">({l.member_count})</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <label style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>{t('Razmak slanja', 'Send interval')}</span>
                        <select value={throttle} onChange={(e) => setThrottle(Number(e.target.value))} style={{ padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }}>
                            <option value={30}>{t('2 / min (30s)', '2 / min (30s)')}</option>
                            <option value={60}>{t('1 / min (60s)', '1 / min (60s)')}</option>
                            <option value={120}>{t('1 / 2 min', '1 / 2 min')}</option>
                            <option value={300}>{t('1 / 5 min', '1 / 5 min')}</option>
                        </select>
                    </label>
                    <label style={{ fontSize: '0.82rem', flex: 1 }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>{t('Ime pošiljatelja (opcionalno)', 'From name (optional)')}</span>
                        <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Auro Domus" style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', flex: 1 }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>Reply-To (opcionalno)</span>
                        <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="marketing@aurodomus.eu" style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }} />
                    </label>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void create()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 18px', borderRadius: 6, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
                        {busy ? '…' : t('Spremi nacrt', 'Save draft')}
                    </button>
                </div>
                <p className="muted" style={{ fontSize: '0.72rem', marginTop: 8 }}>{t('Nakon spremanja, otvori kampanju i klikni "Pokreni slanje" za drip.', 'After saving, open the campaign and click "Start sending" for drip.')}</p>
            </div>
        </div>
    );
}

function CampaignDrawer({ campaignId, token, locale, onClose, onChanged }: { campaignId: number; token: string; locale: Locale; onClose: () => void; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [detail, setDetail] = useState<CampaignDetail | null>(null);
    const [busy, setBusy] = useState(false);

    const load = () => api<CampaignDetail>(`/mailing/campaigns/${campaignId}`, token).then(setDetail).catch(() => undefined);
    useEffect(() => { void load(); }, [campaignId]);
    // Auto-refresh dok šalje
    useEffect(() => {
        if (!detail || !['queued', 'sending'].includes(detail.campaign.status)) return;
        const id = window.setInterval(() => { void load(); }, 8000);
        return () => window.clearInterval(id);
    }, [detail?.campaign.status, campaignId]);

    const act = async (path: string, body: Record<string, unknown> = {}) => {
        setBusy(true);
        try { await mutate(`/mailing/campaigns/${campaignId}/${path}`, token, path === '' ? 'DELETE' : 'POST', body); await load(); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const c = detail?.campaign;
    const [hr, en, color] = c ? (STATUS_LABEL[c.status] ?? [c.status, c.status, '#999']) : ['', '', '#999'];

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(900px, 96vw)', height: '100vh', overflowY: 'auto', background: '#fffdf6' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', padding: '14px 20px', color: '#fffbeb', position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.75 }}>{t('Kampanja', 'Campaign')} #{campaignId}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.1rem' }}>{c?.subject ?? '—'}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '1.05rem' }}>✕</button>
                </div>

                {!detail ? <p className="muted" style={{ padding: 20 }}>{t('Učitavam…', 'Loading…')}</p> : (
                    <div style={{ padding: '14px 22px' }}>
                        {/* Status + akcije */}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                            <span style={{ color, fontWeight: 700, fontSize: '0.95rem' }}>{locale === 'en' ? en : hr}</span>
                            {c && ['draft', 'paused'].includes(c.status) ? (
                                <button type="button" disabled={busy} onClick={() => { if (confirm(t('Pokrenuti slanje kampanje? Šalje se 1-po-1 prema odabranom razmaku.', 'Start sending? One-by-one at the chosen interval.'))) void act('queue'); }}
                                    style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>▶ {t('Pokreni slanje', 'Start sending')}</button>
                            ) : null}
                            {c && ['sending', 'queued'].includes(c.status) ? (
                                <button type="button" disabled={busy} onClick={() => void act('control', { action: 'pause' })}
                                    style={{ background: 'var(--ad-acc-3)', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>⏸ {t('Pauziraj', 'Pause')}</button>
                            ) : null}
                            {c && c.status === 'paused' ? (
                                <button type="button" disabled={busy} onClick={() => void act('control', { action: 'resume' })}
                                    style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>▶ {t('Nastavi', 'Resume')}</button>
                            ) : null}
                            {c && !['sent', 'cancelled'].includes(c.status) ? (
                                <button type="button" disabled={busy} onClick={() => { if (confirm(t('Otkazati kampanju?', 'Cancel campaign?'))) void act('control', { action: 'cancel' }); }}
                                    style={{ background: '#b14040', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>✗ {t('Otkaži', 'Cancel')}</button>
                            ) : null}
                            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#888' }}>
                                {t('Razmak', 'Interval')}: {c?.throttle_seconds}s
                            </span>
                        </div>

                        {/* KPI */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 16 }}>
                            {([
                                [t('Primatelji', 'Recipients'), detail.campaign.total_recipients, BROWN],
                                [t('U redu', 'Queued'), detail.stats.queued, 'var(--ad-acc-3)'],
                                [t('Poslano', 'Sent'), detail.stats.sent, '#1f7a36'],
                                [t('Otvoreno', 'Opened'), detail.stats.opened, GOLD],
                                [t('Greška', 'Failed'), detail.stats.failed, '#b14040'],
                            ] as const).map(([label, val, col], i) => (
                                <div key={i} style={{ padding: 10, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: col }}>{val}</div>
                                    <div style={{ fontSize: '0.72rem', color: '#888' }}>{label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Progress bar */}
                        {detail.campaign.total_recipients > 0 ? (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ height: 10, background: 'rgba(var(--ad-soft-rgb),0.2)', borderRadius: 5, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.round(detail.stats.sent / detail.campaign.total_recipients * 100)}%`, background: 'linear-gradient(90deg, #1f7a36, #2ecc71)', transition: 'width 0.5s' }} />
                                </div>
                                <div style={{ fontSize: '0.74rem', color: '#888', marginTop: 4, textAlign: 'center' }}>
                                    {detail.stats.sent} / {detail.campaign.total_recipients} {t('poslano', 'sent')} · {detail.stats.opened} {t('otvorilo', 'opened')}
                                    {detail.campaign.status === 'sending' ? ` · ${t('šalje se…', 'sending…')}` : ''}
                                </div>
                            </div>
                        ) : null}

                        {/* Per-primatelj tablica */}
                        <h4 style={{ fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)', margin: '8px 0 6px' }}>
                            {t('Primatelji i status čitanja', 'Recipients and read status')}
                        </h4>
                        <div className="table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
                            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                                <thead><tr>
                                    <th>{t('Ime', 'Name')}</th><th>Email</th>
                                    <th>{t('Status', 'Status')}</th><th>{t('Poslano', 'Sent')}</th><th>{t('Otvoreno', 'Opened')}</th>
                                </tr></thead>
                                <tbody>
                                    {detail.sends.map((s) => (
                                        <tr key={s.id}>
                                            <td>{s.name ?? <span className="muted">—</span>}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: '0.74rem' }}>{s.email}</td>
                                            <td>
                                                <span style={{ color: s.status === 'sent' ? '#1f7a36' : s.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)', fontWeight: 700 }}>
                                                    {s.status === 'queued' ? '⏳' : s.status === 'sent' ? '✓' : s.status === 'failed' ? '✗' : s.status}
                                                </span>
                                                {s.failure_note ? <span title={s.failure_note} style={{ color: '#b14040', cursor: 'help' }}> ⚠</span> : null}
                                            </td>
                                            <td className="muted">{s.sent_at ? formatDate(s.sent_at, locale) : '—'}</td>
                                            <td>{s.opened_at ? <strong style={{ color: '#1f7a36' }}>✓ {formatDate(s.opened_at, locale)}{s.open_count > 1 ? ` (${s.open_count}×)` : ''}</strong> : <span className="muted">—</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function MembersModal({ data, locale, onClose }: { data: { list: MailingList; members: Member[] }; locale: Locale; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(600px, 96vw)', maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: BROWN }}>👥 {data.list.name} ({data.members.length})</h3>
                    <button type="button" onClick={onClose} className="secondary compact">✕</button>
                </div>
                <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                    <thead><tr><th>{t('Ime', 'Name')}</th><th>Email</th><th>{t('Tip', 'Type')}</th></tr></thead>
                    <tbody>
                        {data.members.map((m) => (
                            <tr key={m.id} style={{ opacity: m.unsubscribed ? 0.4 : 1 }}>
                                <td>{m.name ?? '—'}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.76rem' }}>{m.email}</td>
                                <td className="muted">{m.member_type ?? '—'}{m.unsubscribed ? ' 🚫' : ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
